import type { UserInfo } from '@/types/auth.types';

export interface OidcTokenClaims {
  sub: string;
  exp: number;
  preferred_username?: string;
  email?: string;
  tenant_id?: string;
}

export interface ParsedOidcToken {
  claims: OidcTokenClaims;
  expiresSec: number;
}

let oidcRedirectPromise: Promise<void> | null = null;

function redirectToOidcUrl(url: string): Promise<void> {
  // 同一页面生命周期内只允许首次 OIDC 导航生效。登出先占用该 Promise 后，
  // 清空 token 引发的 AuthGuard / 401 登录跳转只会复用它，不会覆盖登出地址。
  oidcRedirectPromise ??= Promise.resolve().then(() => {
    window.location.replace(url);
  });
  return oidcRedirectPromise;
}

export function createOidcLoginUrl(origin: string): string {
  const loginUrl = new URL('/api/v1/oidc/login', origin);
  loginUrl.searchParams.set('redirect_origin', origin);
  return loginUrl.toString();
}

export function redirectToOidcLogin(origin: string = window.location.origin): Promise<void> {
  return redirectToOidcUrl(createOidcLoginUrl(origin));
}

export function createOidcLogoutUrl(origin: string, accessToken: string): string {
  const logoutUrl = new URL('/api/v1/oidc/logout', origin);
  logoutUrl.searchParams.set('redirect_origin', origin);
  logoutUrl.searchParams.set('code', accessToken);
  return logoutUrl.toString();
}

export function redirectToOidcLogout(
  accessToken: string,
  origin: string = window.location.origin
): Promise<void> {
  return redirectToOidcUrl(createOidcLogoutUrl(origin, accessToken));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function parseOidcToken(token: string, nowMs: number = Date.now()): ParsedOidcToken {
  const parts = token.split('.');
  if (parts.length !== 3 || parts.some((part) => !part)) {
    throw new Error('回调未返回有效的 token');
  }

  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    throw new Error('token 格式不正确');
  }

  if (!isRecord(payload)) {
    throw new Error('token payload 格式不正确');
  }

  const sub = optionalString(payload.sub);
  const exp = payload.exp;
  if (!sub || typeof exp !== 'number' || !Number.isFinite(exp)) {
    throw new Error('token 缺少必要的身份信息');
  }

  const nowSec = Math.floor(nowMs / 1000);
  if (exp <= nowSec) {
    throw new Error('token 已过期，请重新登录');
  }

  return {
    claims: {
      sub,
      exp,
      preferred_username: optionalString(payload.preferred_username),
      email: optionalString(payload.email),
      tenant_id: optionalString(payload.tenant_id),
    },
    expiresSec: Math.max(1, Math.floor(exp - nowSec)),
  };
}

export function isOidcTokenValid(token: string | null, nowMs: number = Date.now()): boolean {
  if (!token) {
    return false;
  }

  try {
    parseOidcToken(token, nowMs);
    return true;
  } catch {
    return false;
  }
}

export function oidcClaimsToUserInfo(claims: OidcTokenClaims): UserInfo {
  return {
    user_id: claims.sub,
    username: claims.preferred_username || claims.email || claims.sub,
    user_type: 'account',
    account_id: claims.sub,
    domain_id: claims.tenant_id,
  };
}

export function removeOidcCallbackParams(currentUrl: string): string {
  const url = new URL(currentUrl);
  url.searchParams.delete('code');
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  return `${url.pathname}${url.search}${url.hash}`;
}
